// These mappers normalize the payment-service responses (snake_case, integer
// cents) into a clean camelCase shape the FE renders. Fallbacks accept
// camelCase too, in case the gateway transforms. Money formatting lives in
// money.js — mappers only transform shapes.
import { formatDollars } from './money';

export const mapPromotion = (promotion = {}) => {
  const maxValueCents =
    promotion.max_payout_amount_cents ?? promotion.maxPayoutAmountCents ?? 0;
  const disabledAt = promotion.disabled_at ?? promotion.disabledAt ?? null;

  return {
    id: promotion.id,
    name: promotion.name ?? '',
    maxValueCents,
    maxValueDisplay: formatDollars(maxValueCents),
    isDisabled: Boolean(disabledAt),
    createdBy: promotion.created_by ?? promotion.createdBy ?? null,
    createdAt: promotion.created_at ?? promotion.createdAt ?? null,
  };
};

// Normalizes the upload response: { batch_id, queued, skipped: [{professional_id, reason}] }.
// `queued` = rows accepted for async transfer; `skipped` = rows the BE rejected
// (missing_value | bad_format | amount_too_high | unrecognized_professional).
export const mapPayoutUploadResult = (result = {}) => {
  const skipped = Array.isArray(result.skipped) ? result.skipped : [];

  return {
    batchId: result.batch_id ?? result.batchId ?? null,
    queued: result.queued ?? 0,
    skipped: skipped.map((row) => ({
      professionalId: row.professional_id ?? row.professionalId ?? '',
      reason: row.reason ?? 'technical_error',
    })),
  };
};

// Per-professional payout row (endpoint #7): promotion name, when it processed,
// the amount, and the status (queued | succeeded | failed | skipped).
export const mapProfessionalPayout = (payout = {}) => {
  const amountCents = payout.amount_cents ?? payout.amountCents ?? 0;

  return {
    promotionName: payout.promotion_name ?? payout.promotionName ?? '',
    processedAt: payout.processed_at ?? payout.processedAt ?? null,
    amountCents,
    amountDisplay: `$${formatDollars(amountCents)}`,
    status: payout.status ?? '',
  };
};

export const mapPayoutBatch = (batch = {}) => {
  // "n/a" for a batch that never had failures is represented as a nullish
  // failed count; the succeeded/failed counts are eventually consistent per the
  // BE (async order processing), so treat missing as unknown/zero downstream.
  const failedCount = batch.failed_count ?? batch.failedCount ?? null;

  return {
    batchId: batch.batch_id ?? batch.batchId ?? batch.id,
    submittedAt:
      batch.submitted_at ??
      batch.submittedAt ??
      batch.processed_at ??
      batch.processedAt ??
      null,
    // Prefer the resolved display name; the BE falls back to the admin id.
    uploadedBy:
      batch.submitted_by_name ??
      batch.submittedByName ??
      batch.uploaded_by ??
      batch.uploadedBy ??
      batch.submitted_by ??
      batch.submittedBy ??
      '',
    succeededCount: batch.succeeded_count ?? batch.succeededCount ?? 0,
    failedCount,
  };
};
